import { combineLatest, iif, Observable, of, switchMap, timer } from 'rxjs';
import { distinctUntilChanged, exhaustMap, filter, map, takeUntil } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, OnInit, TrackByFunction } from '@angular/core';

import { Store } from '@ngxs/store';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { FlipAnimation } from '@app/core/animations/flip.animation';
import { CustomerApiService } from '@app/core/api/customer-api.service';
import { Account } from '@app/core/models/common.models';
import { Customer } from '@app/core/models/customer.models';
import { LifecycleHooks } from '@app/core/services/lifecycle-hooks.service';
import {
  CancelAccountMove,
  CancelAllAccountMove,
  RefreshAccounts,
} from '@app/core/store/states/accounts/accounts-state.actions';
import { AccountsState } from '@app/core/store/states/accounts/accounts.state';
import { ActionsState } from '@app/core/store/states/actions/actions.state';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { DisplayToast } from '@app/core/store/states/ui/ui-state.actions';
import { arrayUnique, isArrayShallowEqual } from '@app/core/utilities/array.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { ToggleSubject } from '@app/shared/operators/toggle-subject.observable';
import { TranslatePipe } from '@ngx-translate/core';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { ɵNzTransitionPatchDirective } from 'ng-zorro-antd/core/transition-patch';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzTableCellDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { AccordionModule } from '../../../shared/modules/accordion/accordion.module';
import { IconComponent } from '@app/shared/components/icon/icon.component';

@Component({
  selector: 'adspt-account-move-banner',
  templateUrl: 'account-move-banner.component.html',
  styleUrls: ['account-move-banner.component.scss'],
  providers: [LifecycleHooks],
  animations: [FlipAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NzAlertComponent,
    NgFor,
    AccordionModule,
    NzButtonComponent,
    NzWaveDirective,
    ɵNzTransitionPatchDirective,
    NzIconDirective,
    NzTrDirective,
    NzTableCellDirective,
    NzSpinComponent,
    AsyncPipe,
    TranslatePipe,
    IconComponent,
  ],
})
export class AccountMoveBannerComponent implements OnInit {
  readonly showDetails$ = new ToggleSubject();

  readonly scheduledForMove$: Observable<Account[]> = this.store.select(AccountsState.accountsScheduledForMove);

  readonly currentlyMoving$: Observable<Account[]> = this.store.select(AccountsState.accountsCurrentlyMoving);

  readonly canMove$: Observable<boolean> = this.store.select(CustomerState.hasAgencyAdminRights);

  readonly undoingAll$: Observable<boolean> = this.store.select(ActionsState.whileAction(CancelAllAccountMove));

  readonly customerNames$: Observable<Record<number, string>> = combineLatest([
    this.scheduledForMove$,
    this.currentlyMoving$,
  ]).pipe(
    map(([scheduledForMove, currentlyMoving]) =>
      arrayUnique(
        [...scheduledForMove, ...currentlyMoving]
          .filter((account) => account.customerChangeTarget)
          .map((account) => account.customerChangeTarget),
      ),
    ),
    distinctUntilChanged(isArrayShallowEqual),
    switchMap((customerIds) => (customerIds.length ? this.ca.getCustomersById(customerIds) : of([] as Customer[]))),
    map((customers) =>
      customers.reduce(
        (a, b) => ({
          ...a,
          [b.customerId]: b.name,
        }),
        {},
      ),
    ),
  );

  readonly undoScheduledLoading$: Observable<Record<number, boolean>> = this.scheduledForMove$.pipe(
    switchMap((scheduled) =>
      iif(
        () => !!scheduled.length,
        combineLatest(
          scheduled.map((scheduled) =>
            this.store
              .select(ActionsState.whileAction(CancelAccountMove, { adspertAccountId: scheduled.adspertAccountId }))
              .pipe(map((loading) => ({ [scheduled.adspertAccountId]: loading }))),
          ),
        ).pipe(map((scheduledLoading) => scheduledLoading.reduce((a, b) => ({ ...a, ...b }), {}))),
        of({}),
      ),
    ),
  );

  readonly trackByAccount: TrackByFunction<Account> = (_, account: Account) => account.adspertAccountId;

  constructor(
    private ca: CustomerApiService,
    private store: Store,
    private lh: LifecycleHooks,
  ) {}

  ngOnInit(): void {
    this.pollForUpdates();
  }

  protected undoAll(): void {
    const customerId = this.store.selectSnapshot(CustomerState.customerId);

    this.store.dispatch(new CancelAllAccountMove(customerId)).subscribe({
      error: () => this.store.dispatch(new DisplayToast({ title: 'common.error-msg', type: 'error' })),
    });
  }

  protected onUndoMove(adspertAccountId: number): void {
    const loading = lastValue(this.undoScheduledLoading$, {})[adspertAccountId] || lastValue(this.undoingAll$);

    if (!loading) {
      const customerId = this.store.selectSnapshot(CustomerState.customerId);

      this.store.dispatch(new CancelAccountMove(adspertAccountId, customerId)).subscribe({
        error: () => this.store.dispatch(new DisplayToast({ title: 'common.error-msg', type: 'error' })),
      });
    }
  }

  private pollForUpdates(): void {
    combineLatest([this.scheduledForMove$, this.currentlyMoving$])
      .pipe(
        map(([scheduledForMove, currentlyMoving]) =>
          arrayUnique([...scheduledForMove, ...currentlyMoving].map((account) => account.adspertAccountId)),
        ),
        distinctUntilChanged(isArrayShallowEqual),
        filter((adspertAccountIds) => !!adspertAccountIds.length),
        switchMap((adspertAccountIds) =>
          timer(10000, 10000).pipe(
            exhaustMap(() =>
              this.store.dispatch(
                new RefreshAccounts(this.store.selectSnapshot(CustomerState.customerId), adspertAccountIds),
              ),
            ),
          ),
        ),
        takeUntil(this.lh.destroy$),
      )
      .subscribe();
  }
}
